import React from 'react';
import { Col, Divider, Row } from 'antd';
import { isEqual, isNil } from 'lodash';

import {
  CustomerDocumentTypeEnum,
  CustomerHistoryDetailsModel,
  FullCustomerInfoResponseModel,
} from 'api/adminApiClient';
import BaseAlert from 'components/wrappers/BaseAlert';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { getCustomerHistoryFile } from 'redux/reducers/clientSlice';
import { downloadFile, getFileNameWithoutExt } from 'utils/fileUtils';
import {
  getCustomerBreakInServiceLabel,
  getCustomerPreScreeningBilledLabel,
} from 'utils/clientUtils';
import { getYesNoAnswer } from 'components/ActivitiesDetailsSection/HistorySection/utils';
import HistoryLog from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryLog';
import HistoryField from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryField';

import styles from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryCard.module.less';

type Props = {
  customerId: string;
  historyDetails: CustomerHistoryDetailsModel;
  historyId: string;
};

export default function AdditionalInformation3DetailsCard(props: Props) {
  const { customerId, historyDetails, historyId } = props;
  const { oldData, newData } = historyDetails;
  const dispatch = useAppDispatch();
  const hasAssignmentRequirementsChanged = !isEqual(
    oldData?.additionalInfo?.additionalInfo3?.assignmentRequirements,
    newData?.additionalInfo?.additionalInfo3?.assignmentRequirements,
  );
  const hasSpecialAssignmentRequirementsChanged = !isEqual(
    oldData?.additionalInfo?.additionalInfo3?.specialAssignmentRequirements,
    newData?.additionalInfo?.additionalInfo3?.specialAssignmentRequirements,
  );
  const hasIsIndependentContractorsChanged = !isEqual(
    oldData?.additionalInfo?.additionalInfo3?.assignmentRequirements
      ?.isIndependentContractors,
    newData?.additionalInfo?.additionalInfo3?.assignmentRequirements
      ?.isIndependentContractors,
  );
  const hasDesignatedPreScreeningProviderChanged = !isEqual(
    oldData?.additionalInfo?.additionalInfo3?.assignmentRequirements
      ?.designatedPreScreeningProvider,
    newData?.additionalInfo?.additionalInfo3?.assignmentRequirements
      ?.designatedPreScreeningProvider,
  );
  const hasIsPreScreeningRequiredChanged = !isEqual(
    oldData?.additionalInfo?.additionalInfo3?.assignmentRequirements
      ?.isPreScreeningRequired,
    newData?.additionalInfo?.additionalInfo3?.assignmentRequirements
      ?.isPreScreeningRequired,
  );
  const hasPreScreeningBilledToChanged = !isEqual(
    oldData?.additionalInfo?.additionalInfo3?.assignmentRequirements
      ?.preScreeningBilledTo,
    newData?.additionalInfo?.additionalInfo3?.assignmentRequirements
      ?.preScreeningBilledTo,
  );
  const hasPreScreeningChanged = !isEqual(
    oldData?.additionalInfo?.additionalInfo3?.assignmentRequirements
      ?.preScreening,
    newData?.additionalInfo?.additionalInfo3?.assignmentRequirements
      ?.preScreening,
  );
  const hasIsOnboardingDocumentationRequiredChanged = !isEqual(
    oldData?.additionalInfo?.additionalInfo3?.assignmentRequirements
      ?.isOnboardingDocumentationRequired,
    newData?.additionalInfo?.additionalInfo3?.assignmentRequirements
      ?.isOnboardingDocumentationRequired,
  );
  const hasSpecialInstructionsChanged = !isEqual(
    oldData?.additionalInfo?.additionalInfo3?.assignmentRequirements
      ?.specialInstructions,
    newData?.additionalInfo?.additionalInfo3?.assignmentRequirements
      ?.specialInstructions,
  );
  const hasOnboardingDocumentationFilesChanged = !isEqual(
    oldData?.additionalInfo?.additionalInfo3?.assignmentRequirements
      ?.onboardingDocumentationFileKeys,
    newData?.additionalInfo?.additionalInfo3?.assignmentRequirements
      ?.onboardingDocumentationFileKeys,
  );
  const hasTerminationInstructionsChanged = !isEqual(
    oldData?.additionalInfo?.additionalInfo3?.assignmentRequirements
      ?.terminationInstructions,
    newData?.additionalInfo?.additionalInfo3?.assignmentRequirements
      ?.terminationInstructions,
  );
  const hasGuaranteedWorkPerformanceChanged = !isEqual(
    oldData?.additionalInfo?.additionalInfo3?.specialAssignmentRequirements
      ?.guaranteedWorkPerformance,
    newData?.additionalInfo?.additionalInfo3?.specialAssignmentRequirements
      ?.guaranteedWorkPerformance,
  );
  const hasTempToPermConversionChanged = !isEqual(
    oldData?.additionalInfo?.additionalInfo3?.specialAssignmentRequirements
      ?.tempToPermConversion,
    newData?.additionalInfo?.additionalInfo3?.specialAssignmentRequirements
      ?.tempToPermConversion,
  );
  const hasTimeEntryChanged = !isEqual(
    oldData?.additionalInfo?.additionalInfo3?.specialAssignmentRequirements
      ?.timeEntry,
    newData?.additionalInfo?.additionalInfo3?.specialAssignmentRequirements
      ?.timeEntry,
  );
  const hasAddedNotesChanged = !isEqual(
    oldData?.additionalInfo?.additionalInfo3?.specialAssignmentRequirements
      ?.addedNotes,
    newData?.additionalInfo?.additionalInfo3?.specialAssignmentRequirements
      ?.addedNotes,
  );
  const hasBreakInServiceChanged = !isEqual(
    oldData?.additionalInfo?.additionalInfo3?.specialAssignmentRequirements
      ?.breakInService,
    newData?.additionalInfo?.additionalInfo3?.specialAssignmentRequirements
      ?.breakInService,
  );

  const handleOnboardingDocumentationFileDownload = async (
    fileKey?: string,
  ) => {
    const result = await dispatch(
      getCustomerHistoryFile({
        customerId,
        fileKey,
        documentType: CustomerDocumentTypeEnum.OnboardingDocumentation,
      }),
    );

    if (getCustomerHistoryFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const renderAssignmentRequirementsSection = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    const sectionName = 'assignment-requirements';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Assignment requirements</div>
          </Col>

          {hasIsIndependentContractorsChanged && (
            <HistoryField
              title="Independent contractors permitted"
              value={getYesNoAnswer(
                customerDetails?.additionalInfo?.additionalInfo3
                  ?.assignmentRequirements?.isIndependentContractors,
              )}
              dataId={`${sectionName}-independent-contractors-permitted-field`}
            />
          )}

          {hasDesignatedPreScreeningProviderChanged && (
            <HistoryField
              title="Designated pre-screening provider"
              value={getYesNoAnswer(
                customerDetails?.additionalInfo?.additionalInfo3
                  ?.assignmentRequirements?.designatedPreScreeningProvider,
              )}
              dataId={`${sectionName}-designated-pre-screening-provider-field`}
            />
          )}

          {hasIsPreScreeningRequiredChanged && (
            <HistoryField
              title="Pre-screening required"
              value={getYesNoAnswer(
                customerDetails?.additionalInfo?.additionalInfo3
                  ?.assignmentRequirements?.isPreScreeningRequired,
              )}
              dataId={`${sectionName}-pre-screening-required-field`}
            />
          )}

          {hasPreScreeningBilledToChanged && (
            <HistoryField
              title="Pre-screening billed to"
              value={
                !isNil(
                  customerDetails?.additionalInfo?.additionalInfo3
                    ?.assignmentRequirements?.preScreeningBilledTo,
                )
                  ? getCustomerPreScreeningBilledLabel(
                      customerDetails?.additionalInfo?.additionalInfo3
                        ?.assignmentRequirements?.preScreeningBilledTo,
                    )
                  : undefined
              }
              dataId={`${sectionName}-pre-screening-billed-to-field`}
            />
          )}

          {hasPreScreeningChanged && (
            <HistoryField
              title="Pre-screening"
              value={
                customerDetails?.additionalInfo?.additionalInfo3
                  ?.assignmentRequirements?.preScreening
              }
              dataId={`${sectionName}-pre-screening-field`}
            />
          )}

          {hasIsOnboardingDocumentationRequiredChanged && (
            <HistoryField
              title="Onboarding documentation required"
              value={getYesNoAnswer(
                customerDetails?.additionalInfo?.additionalInfo3
                  ?.assignmentRequirements?.isOnboardingDocumentationRequired,
              )}
              dataId={`${sectionName}-onboarding-documentation-required-field`}
            />
          )}

          {hasSpecialInstructionsChanged && (
            <HistoryField
              title="Special instructions"
              value={
                customerDetails?.additionalInfo?.additionalInfo3
                  ?.assignmentRequirements?.specialInstructions
              }
              dataId={`${sectionName}-special-instructions-field`}
            />
          )}

          {hasOnboardingDocumentationFilesChanged && (
            <HistoryField
              title="Onboarding documentation"
              emptyText="No files"
              value={
                customerDetails?.additionalInfo?.additionalInfo3
                  ?.assignmentRequirements?.onboardingDocumentationFileKeys
                  ?.length &&
                customerDetails?.additionalInfo?.additionalInfo3
                  ?.assignmentRequirements?.onboardingDocumentationFileNames
                  ?.length && (
                  <>
                    {customerDetails.additionalInfo?.additionalInfo3.assignmentRequirements.onboardingDocumentationFileKeys
                      .map<React.ReactNode>((fileKey, index) => (
                        <span
                          key={index}
                          className="link-imitation"
                          onClick={() =>
                            handleOnboardingDocumentationFileDownload(fileKey)
                          }
                        >
                          {getFileNameWithoutExt(
                            (
                              customerDetails?.additionalInfo?.additionalInfo3
                                ?.assignmentRequirements
                                ?.onboardingDocumentationFileNames as string[]
                            )[index],
                          )}
                        </span>
                      ))
                      .reduce((previous, current) => [previous, ', ', current])}
                  </>
                )
              }
              dataId={`${sectionName}-onboarding-documentation-field`}
            />
          )}

          {hasTerminationInstructionsChanged && (
            <HistoryField
              title="Termination instructions"
              value={
                customerDetails?.additionalInfo?.additionalInfo3
                  ?.assignmentRequirements?.terminationInstructions
              }
              dataId={`${sectionName}-termination-instructions-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderSpecialAssignmentRequirementSection = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    const sectionName = 'special-assignment-requirements';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>
              Special assignment requirements
            </div>
          </Col>

          {hasGuaranteedWorkPerformanceChanged && (
            <HistoryField
              title="Guaranteed work performance"
              value={
                customerDetails?.additionalInfo?.additionalInfo3
                  ?.specialAssignmentRequirements?.guaranteedWorkPerformance
              }
              dataId={`${sectionName}-guaranteed-work-performance-field`}
            />
          )}

          {hasTempToPermConversionChanged && (
            <HistoryField
              title="Temp-to-perm conversion"
              value={
                customerDetails?.additionalInfo?.additionalInfo3
                  ?.specialAssignmentRequirements?.tempToPermConversion
              }
              dataId={`${sectionName}-temp-to-perm-conversion-field`}
            />
          )}

          {hasTimeEntryChanged && (
            <HistoryField
              title="Time entry method"
              value={
                customerDetails?.additionalInfo?.additionalInfo3
                  ?.specialAssignmentRequirements?.timeEntry
              }
              dataId={`${sectionName}-time-entry-field`}
            />
          )}

          {hasAddedNotesChanged && (
            <HistoryField
              title="Added notes"
              value={
                customerDetails?.additionalInfo?.additionalInfo3
                  ?.specialAssignmentRequirements?.addedNotes
              }
              dataId={`${sectionName}-added-notes-field`}
            />
          )}

          {hasBreakInServiceChanged && (
            <HistoryField
              title="Break in service"
              value={getCustomerBreakInServiceLabel(
                customerDetails?.additionalInfo?.additionalInfo3
                  ?.specialAssignmentRequirements?.breakInService,
              )}
              dataId={`${sectionName}-break-in-service-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderAdditionalInformation = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    return (
      <>
        {hasAssignmentRequirementsChanged &&
          renderAssignmentRequirementsSection(customerDetails)}

        {hasSpecialAssignmentRequirementsChanged &&
          renderSpecialAssignmentRequirementSection(customerDetails)}
      </>
    );
  };

  return (
    <HistoryLog
      historyId={historyId}
      oldData={renderAdditionalInformation(oldData)}
      newData={renderAdditionalInformation(newData)}
    />
  );
}
